import React, { useState, useEffect, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { apiFetch } from '../utils/apiClient';
import { useVendor } from '../contexts/VendorContext';
import CategoryPicker from './CategoryPicker';

interface BookData {
  isbn_13?: string;
  isbn_10?: string;
  title: string;
  authors?: string;
  description?: string;
  publisher?: string;
  publication_date?: string;
  page_count?: number;
  format?: string;
  categories?: string;
  lookup_categories?: string;
  language?: string;
  image_url?: string;
  cover_image_url?: string;
}

interface BookVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  isbn: string;
  onSave: (data: {
    isbn: string;
    selling_price: string;
    condition: string;
    quantity: number;
    notes?: string;
    image?: File;
    // Book fields that can be edited
    title?: string;
    authors?: string;
    publisher?: string;
    publication_date?: string;
    page_count?: number;
    description?: string;
    format?: string;
    categories?: string;
    language?: string;
  }) => Promise<void>;
}

const BookVerificationModal: React.FC<BookVerificationModalProps> = ({
  isOpen,
  onClose,
  isbn,
  onSave,
}) => {
  const { settings } = useVendor();
  const [bookData, setBookData] = useState<BookData | null>(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  // True only when the seller picked/took a photo themselves (auto-fetched
  // lookup covers are handled server-side and are not re-uploaded).
  const [imageIsManual, setImageIsManual] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [compressing, setCompressing] = useState(false);

  // Form fields
  const [sellingPrice, setSellingPrice] = useState('');
  const [condition, setCondition] = useState('new');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [bookLanguage, setBookLanguage] = useState('English');
  const [saving, setSaving] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedBookData, setEditedBookData] = useState<BookData | null>(null);

  const loadAndCompressImage = useCallback(async (imageUrl: string) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = imageUrl;
      });
      
      // Convert image to blob and compress
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], 'book-cover.jpg', { type: 'image/jpeg' });
            const options = {
              maxSizeMB: 0.5,
              maxWidthOrHeight: 800,
              useWebWorker: true,
            };
            const compressedFile = await imageCompression(file, options);
            setImageFile(compressedFile);
            setImageIsManual(false);
            const reader = new FileReader();
            reader.onloadend = () => {
              setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(compressedFile);
          }
        }, 'image/jpeg', 0.9);
      }
    } catch (err) {
      console.error('Error loading image:', err);
    }
  }, []);

  const fetchBookImage = useCallback(async (isbn: string) => {
    try {
      // Try Google Books API for cover image
      const googleResponse = await fetch(
        `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`
      );
      
      if (googleResponse.ok) {
        const googleData = await googleResponse.json();
        if (googleData.items && googleData.items.length > 0) {
          const volumeInfo = googleData.items[0].volumeInfo;
          const imageLinks = volumeInfo.imageLinks;
          
          if (imageLinks) {
            // Try to get large image, fallback to medium or small
            const imageUrl =
              imageLinks.large ||
              imageLinks.medium ||
              imageLinks.small ||
              imageLinks.thumbnail;
            
            if (imageUrl) {
              // Fetch and compress the image
              await loadAndCompressImage(imageUrl);
              return;
            }
          }
        }
      }

      // Fallback: Try Open Library (using proxy or direct URL)
      try {
        const openLibUrl = `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg`;
        // Note: Open Library may have CORS restrictions, so we use the URL directly
        // The browser will handle the image loading
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = openLibUrl;
        });
        
        // Convert image to blob and compress
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(async (blob) => {
            if (blob) {
              const file = new File([blob], 'book-cover.jpg', { type: 'image/jpeg' });
              const options = {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 800,
                useWebWorker: true,
              };
              const compressedFile = await imageCompression(file, options);
              setImageFile(compressedFile);
              setImageIsManual(false);
              const reader = new FileReader();
              reader.onloadend = () => {
                setImagePreview(reader.result as string);
              };
              reader.readAsDataURL(compressedFile);
            }
          }, 'image/jpeg', 0.9);
        }
      } catch (err) {
        console.error('Error loading Open Library image:', err);
      }
    } catch (err) {
      console.error('Error fetching book image:', err);
      // Don't set error for image fetch failures - it's not critical
    }
  }, [loadAndCompressImage]);

  const fetchBookData = useCallback(async () => {
    setFetching(true);
    setError('');

    try {
      // Fetch book data from backend
      const response = await apiFetch(`/api/book/?isbn=${isbn}`);

      if (!response.ok) {
        throw new Error('Failed to fetch book data');
      }

      const data = await response.json();
      
      // If book exists in database, use it
      if (data.source === 'database' && data.book) {
        setBookData(data.book);
        setEditedBookData({ ...data.book });
        if (['English', 'Sinhala', 'Tamil'].includes(data.book.language)) setBookLanguage(data.book.language);
        // Use cover_image_url from backend (ISBNdb) if available
        if (data.book.cover_image_url) {
          loadAndCompressImage(data.book.cover_image_url);
        } else {
          fetchBookImage(data.book.isbn_13 || isbn);
        }
      } else if (data.book) {
        // Use external API data
        setBookData(data.book);
        setEditedBookData({ ...data.book });
        if (['English', 'Sinhala', 'Tamil'].includes(data.book.language)) setBookLanguage(data.book.language);
        // Use cover_image_url from backend (ISBNdb) if available
        if (data.book.cover_image_url) {
          loadAndCompressImage(data.book.cover_image_url);
        } else {
          fetchBookImage(data.book.isbn_13 || isbn);
        }
      } else {
        setError('Book not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch book data');
    } finally {
      setFetching(false);
    }
  }, [isbn, fetchBookImage, loadAndCompressImage]);

  useEffect(() => {
    if (isOpen && isbn) {
      fetchBookData();
    } else {
      // Reset form when modal closes
      setBookData(null);
      setEditedBookData(null);
      setImageFile(null);
      setImageIsManual(false);
      setImagePreview('');
      setSellingPrice('');
      setCondition('new');
      setQuantity(1);
      setNotes('');
      setError('');
      setIsEditing(false);
    }
  }, [isOpen, isbn, fetchBookData]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setCompressing(true);
      
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(file, options);
      setImageFile(compressedFile);
      setImageIsManual(true);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(compressedFile);
    } catch (err) {
      setError('Failed to process image');
    } finally {
      setCompressing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedBookData(bookData ? { ...bookData } : null);
  };

  const handleSaveEdit = () => {
    if (editedBookData) {
      setBookData(editedBookData);
      setIsEditing(false);
    }
  };

  const photoRequired = settings.require_photo !== false;
  const hasOwnPhoto = imageIsManual && !!imageFile;

  const handleSave = async () => {
    if (!sellingPrice) {
      setError('Please enter the selling price');
      return;
    }
    if (photoRequired && !hasOwnPhoto) {
      setError('Please add a photo of your copy of the book before listing it.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Include edited book data in the save payload
      const saveData: any = {
        isbn,
        selling_price: sellingPrice,
        condition,
        quantity,
        notes: notes || undefined,
        image: imageIsManual && imageFile ? imageFile : undefined,
      };

      // Add book fields if they were edited
      if (editedBookData) {
        if (editedBookData.title) saveData.title = editedBookData.title;
        if (editedBookData.authors) saveData.authors = editedBookData.authors;
        if (editedBookData.publisher) saveData.publisher = editedBookData.publisher;
        if (editedBookData.publication_date) saveData.publication_date = editedBookData.publication_date;
        if (editedBookData.page_count) saveData.page_count = editedBookData.page_count;
        if (editedBookData.description) saveData.description = editedBookData.description;
        if (editedBookData.format) saveData.format = editedBookData.format;
        if (editedBookData.categories !== undefined) saveData.categories = editedBookData.categories;
      }
      saveData.language = bookLanguage;

      await onSave(saveData);
      
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save inventory item');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen px-0 pt-4 pb-0 text-center sm:block sm:p-0 sm:items-center">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-h-[90vh] overflow-y-auto sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:max-h-[85vh]">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900">
                {isEditing ? 'Edit Book Information' : 'Review Book Information'}
              </h3>
              <div className="flex items-center gap-2">
                {!isEditing && bookData && (
                  <button
                    onClick={handleEdit}
                    className="text-blue-600 active:text-blue-700 px-3 py-2 text-sm font-medium min-h-[44px] touch-manipulation flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 active:text-gray-500 p-2 -mr-2 min-w-[44px] min-h-[44px] touch-manipulation"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {fetching ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Fetching book data...</p>
              </div>
            ) : error && !bookData ? (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            ) : bookData ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Book Information Display */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                  {/* Book Cover */}
                  <div className="md:col-span-1">
                    <div className="aspect-w-3 aspect-h-4 bg-gray-200 rounded-lg overflow-hidden w-full max-w-xs mx-auto md:max-w-none">
                      {compressing ? (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-sm text-gray-500">Compressing image...</p>
                        </div>
                      ) : imagePreview ? (
                        <img
                          src={imagePreview}
                          alt={bookData.title}
                          className="w-full h-full object-cover"
                        />
                      ) : bookData.cover_image_url ? (
                        <img
                          src={bookData.cover_image_url}
                          alt={bookData.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-sm text-gray-500">No image available</p>
                        </div>
                      )}
                    </div>
                    {imagePreview && !hasOwnPhoto && (
                      <p className="mt-1 text-center text-[11px] text-gray-500">Reference cover — please add a photo of your own copy</p>
                    )}
                    <label className={`mt-2 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 min-h-[44px] text-sm font-semibold cursor-pointer touch-manipulation ${hasOwnPhoto ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-blue-600 text-white active:bg-blue-700'}`}>
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {hasOwnPhoto ? 'Photo added · Retake' : (photoRequired ? 'Take a photo of your copy *' : 'Add a photo')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                    {photoRequired && !hasOwnPhoto && (
                      <p className="mt-1 text-center text-xs text-red-600">A photo of your copy is required to list the book.</p>
                    )}
                  </div>

                  {/* Book Details */}
                  <div className="md:col-span-2 space-y-3">
                    {isEditing ? (
                      // Edit Mode - Editable Fields
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editedBookData?.title || ''}
                            onChange={(e) => setEditedBookData(prev => prev ? { ...prev, title: e.target.value } : null)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Authors
                          </label>
                          <input
                            type="text"
                            value={editedBookData?.authors || ''}
                            onChange={(e) => setEditedBookData(prev => prev ? { ...prev, authors: e.target.value } : null)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                            placeholder="Author One, Author Two"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Publisher
                            </label>
                            <input
                              type="text"
                              value={editedBookData?.publisher || ''}
                              onChange={(e) => setEditedBookData(prev => prev ? { ...prev, publisher: e.target.value } : null)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Publication Date
                            </label>
                            <input
                              type="date"
                              value={editedBookData?.publication_date || ''}
                              onChange={(e) => setEditedBookData(prev => prev ? { ...prev, publication_date: e.target.value } : null)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Page Count
                            </label>
                            <input
                              type="number"
                              value={editedBookData?.page_count || ''}
                              onChange={(e) => setEditedBookData(prev => prev ? { ...prev, page_count: parseInt(e.target.value) || undefined } : null)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Format
                            </label>
                            <select
                              value={editedBookData?.format || 'paperback'}
                              onChange={(e) => setEditedBookData(prev => prev ? { ...prev, format: e.target.value } : null)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border min-h-[44px]"
                            >
                              <option value="paperback">Paperback</option>
                              <option value="hardcover">Hardcover</option>
                              <option value="ebook">E-book</option>
                              <option value="audiobook">Audiobook</option>
                            </select>
                          </div>
                        </div>
                        <CategoryPicker
                          value={editedBookData?.categories || ''}
                          onChange={(v) => setEditedBookData(prev => prev ? { ...prev, categories: v } : null)}
                        />
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Description
                          </label>
                          <textarea
                            value={editedBookData?.description || ''}
                            onChange={(e) => setEditedBookData(prev => prev ? { ...prev, description: e.target.value } : null)}
                            rows={4}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-2.5 border"
                            placeholder="Book description..."
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium active:bg-blue-700 min-h-[44px] touch-manipulation"
                          >
                            Save Changes
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium active:bg-gray-300 min-h-[44px] touch-manipulation"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode - Read-only Display
                      <>
                        <h4 className="text-lg sm:text-xl font-semibold text-gray-900">
                          {bookData.title}
                        </h4>
                        {bookData.authors && (
                          <p className="text-sm text-gray-600">
                            <strong>Authors:</strong> {bookData.authors}
                          </p>
                        )}
                        {bookData.publisher && (
                          <p className="text-sm text-gray-600">
                            <strong>Publisher:</strong> {bookData.publisher}
                          </p>
                        )}
                        {bookData.publication_date && (
                          <p className="text-sm text-gray-600">
                            <strong>Published:</strong> {bookData.publication_date}
                          </p>
                        )}
                        {bookData.page_count && (
                          <p className="text-sm text-gray-600">
                            <strong>Pages:</strong> {bookData.page_count}
                          </p>
                        )}
                        {bookData.format && (
                          <p className="text-sm text-gray-600">
                            <strong>Format:</strong> {bookData.format}
                          </p>
                        )}
                        <div className="mt-3">
                          <CategoryPicker
                            value={bookData.categories || ''}
                            onChange={(v) => {
                              setBookData(prev => prev ? { ...prev, categories: v } : null);
                              setEditedBookData(prev => prev ? { ...prev, categories: v } : { ...bookData, categories: v });
                            }}
                            hint={
                              bookData.lookup_categories
                                ? bookData.categories
                                  ? `Picked automatically from the book data (${bookData.lookup_categories}). Tap to change.`
                                  : `Book data says "${bookData.lookup_categories}" — no matching store category, please pick one.`
                                : undefined
                            }
                          />
                        </div>
                        {bookData.description && (
                          <div className="mt-2">
                            <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-wrap">
                              {bookData.description}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Selling price (Rs.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={sellingPrice}
                      onChange={(e) => setSellingPrice(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Book language
                    </label>
                    <select
                      value={bookLanguage}
                      onChange={(e) => setBookLanguage(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border min-h-[44px]"
                    >
                      <option value="English">English</option>
                      <option value="Sinhala">Sinhala</option>
                      <option value="Tamil">Tamil</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Condition
                    </label>
                    <select
                      value={condition}
                      onChange={(e) => setCondition(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border min-h-[44px]"
                    >
                      <option value="new">New</option>
                      <option value="like_new">Like New</option>
                      <option value="very_good">Very Good</option>
                      <option value="good">Good</option>
                      <option value="acceptable">Acceptable</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-2.5 border"
                      placeholder="Additional notes about this book..."
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2 sm:gap-0">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !bookData || !sellingPrice || (photoRequired && !hasOwnPhoto)}
              className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-3 sm:py-2 bg-blue-600 text-base font-medium text-white active:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
            >
              {saving ? (settings.auto_sync ? 'Saving & listing on BookMarketLk...' : 'Saving...') : (settings.auto_sync ? 'Save & list on BookMarketLk' : 'Save to Inventory')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-3 sm:py-2 bg-white text-base font-medium text-gray-700 active:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm min-h-[44px] touch-manipulation"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookVerificationModal;

